/**
 * SidebarController class manages the sidebar functionality, including grouping sheets,
 * sorting by status, and managing CSS classes for columns.
 */
class SidebarStatusmapOverviewController {
    /**
     * Constructs a new SidebarStatusmapOverviewController instance.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Function} $compile - AngularJS compile service.
     * @param {Object} $signals - Signal service for communication.
     * @param {Object} $translate - Translation service.
     */
    constructor($scope, $compile, $signals, $translate) {
        this.$scope = $scope;
        this.$compile = $compile;
        this.$signals = $signals;
        this.$translate = $translate;

        // Internal variables
        this.maxColumnLength = 5;  // Items/Sheets per Column in Overview
        this.maxElementLastColumn = 4;  // Last column includes the map legend as well, so if we need extra space..here please
        this.needFourthColumn = false; // To implement somewhere.. but let's stay easy, just three columns

        // Scope internal variables
        this.task = this.$scope.task;
        this.sheets = this.task.getSheets();
        this.sheetsCount = this.sheets.length;
        this.question = this.$scope.question;

        this.$scope.sheetsCount = this.sheetsCount;
        this.$scope.maxColumnLength = this.maxColumnLength;
        this.translationPath = 'MODULES.SIDEBAR.MAPOVERVIEW.LEGEND.';
        this.$scope.statusTitles = [
            { 'text': 'COMPLETED', 'classname': 'completed' },
            { 'text': 'PARTLY-COMPLETED', 'classname': 'partly-completed' },
            { 'text': 'NOTSTARTED', 'classname': 'not-started' }
        ];

        this.$scope.getStatusTitleByClassName = this.getStatusTitleByClassName.bind(this);
        this.$scope.paneAvailableColumnCssClassNames = ['col-1', 'col-2', 'col-3', 'col-4'];
        this.$scope.paneAvailableColumnNames = [
            { 'classname': 'one', 'length': this.maxColumnLength },
            { 'classname': 'two', 'length': this.maxColumnLength },
            { 'classname': 'three', 'length': this.maxColumnLength },
            { 'classname': 'four', 'length': this.maxElementLastColumn }
        ];

        this.link(this.$scope, null, null);
    }

    /**
     * Retrieves the status title based on the class name.
     * @param {string} value - The class name to look up.
     * @returns {string} The translated status title.
     */
    getStatusTitleByClassName(value) {
        for (let key in this.$scope.statusTitles) {
            if (this.$scope.statusTitles[key].classname == value) {
                return this.$translate.instant(this.translationPath + this.$scope.statusTitles[key].text);
            }
        }
        return '';
    }

    /**
     * Groups an array into sub-arrays of a specified size.
     * @param {Array} arr - The array to group.
     * @param {number} size - The size of each group.
     * @returns {Array} An array of grouped arrays.
     */
    getGroups(arr, size) {
        return [].concat.apply([],
            arr.map((elem, idx) => idx % size ? [] : [arr.slice(idx, idx + size)])
        );
    }

    /**
     * Determines the CSS class name for a pane based on its size.
     * @param {number} size - The size of the pane.
     * @returns {Object} An object containing the CSS class name and a flag indicating if the fourth column is used.
     */
    getPaneCssName(size) {
        let name,
            useFourthColumn = false;
        switch (size) {
            case 1: name = this.$scope.paneAvailableColumnCssClassNames[0]; break;
            case 2: name = this.$scope.paneAvailableColumnCssClassNames[1]; break;
            case 3: name = this.$scope.paneAvailableColumnCssClassNames[2]; break;
            case 4: name = this.$scope.paneAvailableColumnCssClassNames[3]; useFourthColumn = true; break;
            default: name = this.$scope.paneAvailableColumnCssClassNames[3]; break;
        }
        return { 'name': name, 'useFourthColumn': useFourthColumn };
    }

    /**
     * Retrieves a group from an array based on a specified range.
     * @param {Array} arr - The array to retrieve from.
     * @param {number} startPos - The starting position of the range.
     * @param {number} endPos - The ending position of the range.
     * @returns {Array} The group of elements within the specified range.
     */
    getGroupByRange(arr, startPos, endPos) {
        return arr.slice(startPos, endPos);
    }

    /**
     * Groups sheets sorted by their status.
     * @param {number} wantedSize - The desired size of each group.
     * @returns {Array} An array of grouped sheets sorted by status.
     */
    getGroupsSortedByStatus(wantedSize) {
        let arr = this.task.getFinishedSheets();
        arr = arr.concat(this.task.getStartedSheets(), this.task.getNotStartedSheets());

        let groups = [],
            currentGroup = [],
            currentStatus = '', // Finished as starting status
            idx;

        for (idx = 0; idx < arr.length; idx++) {
            if (idx % wantedSize == 0) {
                if (idx > 0) {
                    groups.push(currentGroup);
                }
                currentGroup = [];
            }

            if (currentStatus != arr[idx].getTotalProgressString()) {
                currentStatus = arr[idx].getTotalProgressString();
                currentGroup.push(currentStatus);
                currentGroup.push(arr[idx]);
            } else {
                currentGroup.push(arr[idx]);
            }
        }
        groups.push(currentGroup);

        return groups;
    }

    /**
     * Links the controller to the scope and watches for changes in the sort order.
     * @param {Object} scope - The AngularJS scope object.
     * @param {Object} element - The DOM element.
     * @param {Object} attrs - The attributes of the DOM element.
     */
    link(scope, element, attrs) {
        this.$scope.$watch(
            function () {
                return $('.tab-pane').attr('data-sortby');
            },
            (newSort, oldSort) => {
                if (typeof newSort === 'undefined' && typeof oldSort === 'undefined') {
                    newSort = 'order';
                } else if (typeof newSort === 'undefined' && typeof oldSort !== 'undefined') {
                    newSort = oldSort; //'order';
                }
                let selector = '#myStatusMapTab';

                if (newSort == 'status') {
                    this.$scope.groups = [];
                    this.$scope.paneCss = this.getPaneCssName(3); // Always four cols

                    if (this.sheetsCount <= this.maxColumnLength) {
                        // One status per column
                        this.$scope.groups.push(this.task.getFinishedSheets());
                        this.$scope.groups.push(this.task.getStartedSheets());
                        this.$scope.groups.push(this.task.getNotStartedSheets());
                        this.$scope.legendClassName = this.$scope.paneAvailableColumnNames[3].classname;
                    } else {
                        // By STATUS with more elements than the column height
                        this.$scope.groups = this.getGroupsSortedByStatus(6);
                        this.$scope.paneCss = this.getPaneCssName(this.$scope.groups.length);
                        this.$scope.legendClassName = this.$scope.groups.length < 4 ?
                            this.$scope.paneAvailableColumnNames[this.$scope.groups.length].classname :
                            this.$scope.paneAvailableColumnNames[3].classname;
                    }

                    $(selector).find('.statusmap-button-tab').removeClass('active');
                    $(selector).find('.by-status').addClass('active');
                } else {
                    // By ORDER
                    let columnsNumber = Math.ceil(this.sheetsCount / this.maxColumnLength); // 9Sheets/8=1.125 means 2 columns
                    this.$scope.groups = this.getGroups(this.sheets, this.maxColumnLength);
                    this.$scope.paneCss = this.getPaneCssName(columnsNumber);
                    this.$scope.legendClassName = columnsNumber < 4 ?
                        this.$scope.paneAvailableColumnNames[columnsNumber].classname :
                        this.$scope.paneAvailableColumnNames[3].classname;

                    $(selector).find('.statusmap-button-tab').removeClass('active');
                    $(selector).find('.by-order').addClass('active');
                }

                this.$scope.currentSortType = newSort;
            }
        );
    }
}

export default SidebarStatusmapOverviewController;